/**
 * A birth recorded against the cycle Home is showing.
 *
 * A baby being born is what a positive result was always heading towards, so the
 * record keeps `type: "pregnancy"` and states the birth in `pregnancyOutcome`.
 * Nothing about the type changes — which is why every surface that describes a
 * result has to read this field itself, and why the outcome Home did not: it
 * resolves its copy from the route (`ivf-outcome-positive`) and the route is the
 * same one a beta drawn this morning produces. So a patient whose child had been
 * born was met with "Take a breath after your IVF transfer — you don't have to
 * decide what comes next today", and, once she had acknowledged the result, an
 * offer to turn on pregnancy follow-up.
 *
 * This decides only whether the cycle carries a birth, and how many babies it
 * says. Pure and separate from the component on purpose: it is the rule that
 * decides whether the app congratulates someone, and a rule like that should be
 * assertable without rendering a homepage.
 *
 * A stillbirth is deliberately not here. It is recorded as a
 * `MiscarriageOutcomeKind`, which resolves to an adverse ending, and it must
 * never reach celebratory copy.
 */

import { resolvePostProcedureOutcomeClinicalState } from "../../cycles/presenters/postProcedureOutcomeState";

/** The outcome fields this reads, named as the cycle API names them. */
export type RecordedBirthOutcome = {
  type?: string | null;
  resultStatus?: string | null;
  /** `entered_in_error` — the result was retracted rather than corrected. */
  recordStatus?: string | null;
  verificationStatus?: string | null;
  /** A birth: `live_birth` or `multiple_live_birth`. */
  pregnancyOutcome?: string | null;
  /** A loss, in any of its recorded forms. */
  miscarriageOutcome?: string | null;
  /** How many babies, when she chose to say. */
  liveBirthCount?: number | null;
};

export type RecordedBirth = {
  /**
   * More than one baby.
   *
   * Read from the kind first, because that is what the recording writes: the
   * update step sends `multiple_live_birth` when the count is above one. The
   * count is honoured too, so a record that carries a number without the plural
   * kind — a correction, or a clinic import — still reads as one.
   */
  isMultiple: boolean;
  /** The number the record states, or null when she did not say. */
  liveBirthCount: number | null;
};

const LIVE_BIRTH_KINDS: ReadonlySet<string> = new Set([
  "live_birth",
  "multiple_live_birth",
]);

/**
 * Whether this cycle's recorded outcome is a birth.
 *
 * Returns null for everything else, including a pregnancy that is still ongoing:
 * the caller then keeps the copy it already had.
 *
 * `resolvePostProcedureOutcomeClinicalState` is asked first, and it is the same
 * authority every other reader of this record uses. It covers the ways a positive
 * stops being one — retracted as entered in error, refuted or conflicting,
 * cancelled, withdrawn — and a birth recorded against a result in any of those
 * states must not be celebrated, because the result it belongs to is no longer
 * standing.
 */
export function resolveRecordedBirth(
  outcome: RecordedBirthOutcome | null | undefined,
): RecordedBirth | null {
  if (!outcome) return null;
  if (resolvePostProcedureOutcomeClinicalState(outcome) !== "pregnancy") {
    return null;
  }
  // A loss recorded on the same row wins. Both fields being set is a
  // contradiction the write layer clears, but reading it the other way round
  // would congratulate someone on a pregnancy that ended.
  if (outcome.miscarriageOutcome) return null;
  if (!outcome.pregnancyOutcome) return null;
  if (!LIVE_BIRTH_KINDS.has(outcome.pregnancyOutcome)) return null;

  const count =
    typeof outcome.liveBirthCount === "number" && outcome.liveBirthCount > 0
      ? outcome.liveBirthCount
      : null;

  return {
    isMultiple:
      outcome.pregnancyOutcome === "multiple_live_birth" ||
      (count !== null && count > 1),
    liveBirthCount: count,
  };
}

const I18N_ROOT = "homepage.recorded_birth";

/**
 * The copy a recorded birth is announced in.
 *
 * Returns keys rather than strings, the way `resolveCycleClosingCopy` does, so
 * the mapping can be asserted without a translation function and so both
 * locales are reached through the same call at render.
 *
 * The supporting voice is not a nicety. A partner sees this cycle too, and
 * closing it belongs to the person who carried it — the recorded-result card
 * already says so ("Closing the cycle is theirs to do"). Told in her words, the
 * hero would hand him an instruction that is not his, so his body congratulates
 * them both and says nothing about closing.
 */
export function recordedBirthCopyKeys(input: {
  birth: RecordedBirth;
  viewerIsSupporting: boolean;
}): { titleKey: string; bodyKey: string } {
  const title = input.birth.isMultiple ? "babies_were_born" : "a_baby_was_born";
  const body = input.viewerIsSupporting
    ? "supporting.congratulations_to_you_both"
    : "congratulations_close_whenever_you_are_ready";

  return {
    titleKey: `${I18N_ROOT}.${title}`,
    bodyKey: `${I18N_ROOT}.${body}`,
  };
}

/**
 * The copy the closing card at the foot of Home is given after a birth.
 *
 * That card is the page's last word, and on a positive result it is about the
 * result's future: "What comes next, at your pace", a checklist item reading
 * "Review follow-up timing", and a chat seeded with "help me prepare for
 * follow-up after my positive result". After a birth the follow-up it means has
 * already happened. Its secondary action was the pregnancy-tracking setup, which
 * is the same offer the hero stopped making.
 *
 * What is still true is what the card says here: the record stays hers, the cycle
 * joins her history when she closes it, and Louise will hold a question until she
 * wants to ask it. Nothing is addressed only to the person who carried the cycle,
 * so this needs no supporting voice — the closing line describes what happens
 * rather than telling anyone to do it.
 */
export function recordedBirthHandoffCopyKeys(): {
  titleKey: string;
  bodyKey: string;
  ctaKey: string;
  checklistKeys: readonly [string, string];
  chatPromptKey: string;
} {
  return {
    titleKey: `${I18N_ROOT}.keep_this_with_your_records`,
    bodyKey: `${I18N_ROOT}.the_record_stays_and_louise_holds_your_questions`,
    ctaKey: `${I18N_ROOT}.ask_louise_your_questions`,
    checklistKeys: [
      `${I18N_ROOT}.the_result_stays_with_your_records`,
      `${I18N_ROOT}.the_cycle_joins_your_history_once_closed`,
    ],
    chatPromptKey: `${I18N_ROOT}.chat_prompt_after_a_birth`,
  };
}
